import {
  Card,
  Button,
  Divider,
  Accordion,
  Checkbox,
  TextField,
  OutlinedInput,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
} from "@mui/material";
import { Grid, IconButton } from "@mui/material";
import GridListItem from "components/common/grid/GridListItem";

import {
  onChangeClinicNotes,
  changeExpansion,
  onReportFINChange,
  onClinicEsigChange,
} from "app/store/transmission/slice/transmissionSlice";

import React, { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  Mic as MicIcon,
  Notes as NotesIcon,
  ExpandMore as ExpandMoreIcon,
  SpeakerNotes as SpeakerNotesIcon,
} from "@mui/icons-material";

import { truncate } from "utils/string";
import SubmitButton from "components/Button/SubmitButton";
import useClinicNotes from "components/pages/transmissions/modal/detail/notes/clinic_notes/useClinicNotes";
import {
  USER_ROLES,
  CLINIC_NAMES,
  CLINIC_ROLES,
  CLINIC_ACTIVE,
  HF_BILLING_CODES,
  CLINIC_FOR_REVIEW,
} from "static/constants";
import { formatLocalDateTime } from "utils/date";
import { getCurrentUserRole } from "utils/currentUser.util";
import ChfScreening from "../chf_screening/ChfScreening";
import QuickTextTemplates from "../quick_text_templates/QuickTextTemplates";
import {
  NOTE_SAVE_HANDLERS,
  useRegisterNoteSaveHandler,
} from "../NoteSaveHandlersContext";
import useNoteButtonActionInProgress from "../footer/hooks/useNoteButtonActionInProgress";

const ClinicNotes = ({ data, role, clinicName, physician }) => {
  const {
    theme,
    dispatch,
    reportFIN,
    reportDetail,
    clinicNotes,
    newClinicNote,
    handleInputChange,
    flushClinicNote,
    clinicNotesEsig,
    cancelClinicNotes,
    onSaveClinicNotes,
    clinicNotesTouched,
    isSpeechRecognizing,
    clinicNotesExpanded,
    clinicNoteSubmitting,
    clinicNotesEsigDisabled,
    toggleSpeechRecognition,
  } = useClinicNotes(data);

  const isActionInProgress = useNoteButtonActionInProgress();
  const { accountsMapper } = useSelector((state) => state.account);
  const { reportCurrentTab } = useSelector((state) => state.transmission);
  const { isArchiveReport } = useSelector((state) => state.archiveReport);

  const userRole = getCurrentUserRole();

  const showCHFScreening =
    accountsMapper?.[data?.account]?.settings?.show_chf_screening &&
    HF_BILLING_CODES.includes(data?.billing_code);

  const isPhysician = userRole === USER_ROLES.PHYSICIAN;
  const isClinicUser = [USER_ROLES.CLINIC_ADMIN, USER_ROLES.CLINIC].includes(
    userRole
  );

  const isActiveTab = reportCurrentTab === CLINIC_ACTIVE;
  const isReviewTab = reportCurrentTab === CLINIC_FOR_REVIEW;

  const canEditClinicNotes =
    (isClinicUser && isActiveTab) ||
    (isPhysician && (isActiveTab || isReviewTab));

  // Nothing on an archived, locked or legacy report can be changed
  const isReportReadOnly = isArchiveReport || data?.locked || data?.legacy;
  const isFinReadOnly = isReportReadOnly || !canEditClinicNotes;
  // A read-only clinic note shows as text; while an action runs the box stays, only disabled
  const isClinicNoteReadOnly = isFinReadOnly || clinicNotesEsig;
  // Clinic users can dictate where they can edit the clinic note; never on signed reports
  const canDictateClinicNote = !isClinicNoteReadOnly && !data?.eSig;

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.CLINIC_NOTE_SAVE,
    onSaveClinicNotes
  );

  useRegisterNoteSaveHandler(NOTE_SAVE_HANDLERS.CLINIC_NOTE_UNSAVED, () => {
    // Flush text still in the debounce
    flushClinicNote();
    return (
      !isFinReadOnly &&
      (clinicNotesTouched || (newClinicNote || "") !== (clinicNotes || ""))
    );
  });

  const handleTemplateClick = useCallback(
    (value) => {
      dispatch(
        onChangeClinicNotes({
          note: newClinicNote ? `${newClinicNote}\n${value}` : value,
          notesTouched: true,
        })
      );
    },
    [dispatch, newClinicNote]
  );

  return (
    <>
      {role === "clinic" && (
        <Accordion
          className="view-pdf__panel"
          expanded={clinicNotesExpanded}
          onChange={(e, expanded) => {
            dispatch(changeExpansion({ type: "clinicNotes", value: expanded }));
          }}
          style={{
            boxShadow: "none",
            borderColor:
              theme.palette.type === "dark"
                ? "rgba(255, 255, 255, 0.12)"
                : "#E0E0E0",
          }}
        >
          <AccordionSummary
            expandIcon={
              <ExpandMoreIcon data-action="expand-more-clinic-notes-icon" />
            }
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            <span
              className="view-pdf__notes-header--icon"
              style={{ margin: 0 }}
            >
              <SpeakerNotesIcon />
            </span>
            <div
              className="view-pdf__notes-header--title ml-2"
              style={{ margin: 0, fontSize: "14px" }}
            >
              {data?.account ? truncate(data?.account, 50) : ""}
            </div>
          </AccordionSummary>
          <AccordionDetails className="view-pdf__details">
            {showCHFScreening && (
              <ChfScreening
                reportDetail={reportDetail}
                isActiveOrSingleTab={isActiveTab}
              />
            )}

            {!isArchiveReport &&
              !data?.locked &&
              !data?.legacy &&
              !clinicNotesEsig &&
              canEditClinicNotes && (
                <QuickTextTemplates
                  account={data?.account}
                  onTemplateClick={handleTemplateClick}
                  disabled={isActionInProgress}
                />
              )}

            <Card
              style={{
                margin: "12px 0",
                boxShadow: "none",
                border: "1px solid #E0E0E0",
              }}
            >
              <Grid container className="d-flex align-items-center p-2">
                <Grid item xs={1}>
                  <NotesIcon className="mr-2" />
                </Grid>
                <Grid item xs={11}>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#444444",
                      fontWeight: 500,
                    }}
                  >
                    Clinical Notes
                  </span>
                </Grid>
              </Grid>
              <Divider className="mb-2" />

              <div className="p-3">
                {!isClinicNoteReadOnly ? (
                  <div className="d-flex mt-2">
                    <OutlinedInput
                      rows={3}
                      multiline
                      fullWidth
                      className="view-pdf__textarea"
                      variant="outlined"
                      id="clinic-note"
                      data-text="clinic-note-field"
                      placeholder="Write notes here"
                      value={newClinicNote}
                      inputProps={{
                        spellCheck: true,
                      }}
                      onChange={(e) => handleInputChange(e.target.value)}
                      disabled={isActionInProgress}
                      style={{
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.background.paper,
                      }}
                      endAdornment={
                        // Stays while listening so dictation can always be stopped
                        canDictateClinicNote || isSpeechRecognizing ? (
                          <IconButton
                            color={isSpeechRecognizing ? "primary" : "default"}
                            onClick={toggleSpeechRecognition}
                            disabled={isActionInProgress}
                            className={
                              isSpeechRecognizing ? "mic-pulse" : undefined
                            }
                            aria-label={
                              isSpeechRecognizing
                                ? "Listening..."
                                : "Start voice dictation"
                            }
                          >
                            <MicIcon />
                          </IconButton>
                        ) : (
                          <></>
                        )
                      }
                    />
                  </div>
                ) : (
                  <div className="view-pdf__textarea view-mode mb-0 p-0 border-none">
                    {newClinicNote}
                  </div>
                )}

                {[CLINIC_NAMES.MIAMI].includes(clinicName) &&
                  CLINIC_ROLES.includes(role) &&
                  (isFinReadOnly ? (
                    <Grid container className="mt-3">
                      <GridListItem
                        size={12}
                        title="FIN"
                        subTitle={reportFIN}
                      />
                    </Grid>
                  ) : (
                    <TextField
                      id="fin"
                      label="FIN"
                      className="view-pdf__input"
                      margin="none"
                      variant="outlined"
                      onChange={(e) =>
                        dispatch(
                          onReportFINChange({
                            fin: e.target.value,
                            notesTouched: true,
                          })
                        )
                      }
                      fullWidth
                      value={reportFIN}
                      disabled={isActionInProgress}
                    />
                  ))}

                {!data?.legacy &&
                  clinicName === "Denver Heart" &&
                  physician &&
                  isReviewTab && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          style={
                            clinicNotesEsigDisabled ? { opacity: ".6" } : null
                          }
                          checked={clinicNotesEsig}
                          disabled={clinicNotesEsigDisabled}
                          onChange={(e) =>
                            dispatch(
                              onClinicEsigChange({
                                clinicName,
                                eSig: e.target.value,
                              })
                            )
                          }
                          value={clinicNotesEsig}
                          color="secondary"
                          inputProps={{ "aria-label": "primary checkbox" }}
                        />
                      }
                      label="E-signature"
                      disabled={isArchiveReport}
                    />
                  )}

                <div className="process-buttons__wrapper">
                  <div className="d-flex justify-between">
                    <div
                      className="view-pdf__notes-signature"
                      style={{ marginBottom: "8px" }}
                    >
                      {reportDetail?.clinicSignature?.name && (
                        <>
                          <span>{reportDetail?.clinicSignature?.name}</span>{" "}
                          •&nbsp;
                        </>
                      )}
                      {reportDetail?.clinicSignature?.updated_at && (
                        <span>
                          {formatLocalDateTime(
                            reportDetail?.clinicSignature?.updated_at
                          )}
                        </span>
                      )}
                    </div>
                    {!data.legacy &&
                      !data?.locked &&
                      !isArchiveReport &&
                      canEditClinicNotes && (
                        <div
                          className="d-flex align-items-center flex-end"
                          style={{ gap: "10px", marginBottom: "8px" }}
                        >
                          <Button
                            onClick={cancelClinicNotes}
                            className="view-pdf__button"
                            variant="outlined"
                            color="secondary"
                            disabled={isActionInProgress}
                          >
                            Cancel
                          </Button>

                          <SubmitButton
                            handleClick={() => onSaveClinicNotes()}
                            className="view-pdf__button"
                            variant="contained"
                            color="secondary"
                            isLoading={clinicNoteSubmitting}
                            style={{
                              opacity: !clinicNotesTouched ? ".3" : "1",
                            }}
                            disabled={!clinicNotesTouched || isActionInProgress}
                          />
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </Card>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

export default ClinicNotes;
